import { action, makeObservable, observable } from 'mobx';

import { IApiClient } from '../../../app/data/client';
import { BasePageState } from '../../../app/shared/BasePageState';
import { INavigationService } from '../../../app/shared/NavigationService';
import { RelativeUrl } from '../../../app/shared/Url';
import { ProductDetailsTranslation } from '../../localization/ModuleTranslation';
import type { GetProductModelDetailsQueryResponse, PricingContext } from '../../model';
import { ModuleRoutes } from '../../ModuleRoutes';
import { GetProductModelDetailsQuery } from '../../queries';
import AddProductsToPricelistModalState from '../../shared/AddProductsToPricelistModal/AddProductsToPricelistModalState';
import ExportPricelistToPdfModalState from '../../shared/ExportPricelistToPdfModal/ExportPricelistToPdfModalState';
import TermsModalState from './TermsModal/TermsModalState';

export default class ProductDetailsState extends BasePageState<never> {
  @observable.ref
  public data: GetProductModelDetailsQueryResponse | null = null;

  @observable.ref
  public termsModal: TermsModalState | null = null;

  public readonly addToPricelistModal: AddProductsToPricelistModalState;

  public readonly exportToPdfModal: ExportPricelistToPdfModalState;

  public constructor(
    private readonly client: IApiClient,
    private readonly pricingContext: PricingContext,
    public readonly translation: ProductDetailsTranslation,
    private readonly routes: ModuleRoutes,
    private readonly navigation: INavigationService,
    private readonly id: string,
  ) {
    super(translation);

    makeObservable(this);

    this.addToPricelistModal = new AddProductsToPricelistModalState(
      this.client,
      this.translation.addToPricelistModal,
      () => [id],
    );
    this.exportToPdfModal = new ExportPricelistToPdfModalState(this.client, this.pricingContext, () => ({
      productIds: [this.id],
      locale: this.locale,
    }));
  }

  public get backUrl(): RelativeUrl {
    const { referrer } = this.navigation;
    return this.routes.isListing(referrer) ? referrer : this.routes.listing();
  }

  public get currencyCode() {
    return this.pricingContext.currency;
  }

  public get locale(): string {
    return this.pricingContext.languageCode;
  }

  public async onLoad(): Promise<void> {
    const productModelDetails = await this.client.send(
      new GetProductModelDetailsQuery({
        pricingContext: this.pricingContext,
        productId: this.id,
      }),
    );

    this.initialize(productModelDetails);
  }

  @action.bound
  private initialize(data: GetProductModelDetailsQueryResponse): void {
    this.data = data;
    this.termsModal = new TermsModalState(this.client, this.pricingContext, this.translation.terms, {
      id: data.productDetails.brandId,
      name: data.productDetails.brandName,
    });
  }
}
